/**
 * Main menu page component
 */
import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertCircle } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui'
import { StickyTopBar } from '@/components/layout/StickyTopBar'
import { RestaurantHeader } from '@/components/layout/RestaurantHeader'
import { LocationPickerSheet } from '@/components/layout/LocationPickerSheet'
import { Footer } from '@/components/layout/Footer'
import { ItemCustomizationModal } from '@/components/customization'
import { CartSidebar, FloatingCartButton } from '@/components/cart'
import { CheckoutFAQ } from '@/components/checkout'
import {
  CategoryNav,
  MenuSection,
  MenuLoadingSkeleton,
  FeaturedCarousel,
} from '@/components/menu'
import { useTransformedMenuData } from '@/hooks/useTransformedMenuData'
import { useMenuData } from '@/hooks/useMenuData'
import { useMenuDefaults } from '@/hooks/useMenuDefaults'
import { MenuItem } from '@/types/menu'

interface MenuPageProps {
  onNavigate: (path: string) => void
}

export function MenuPage({ onNavigate }: MenuPageProps) {
  const { t } = useTranslation()
  useMenuDefaults()
  const { collections, itemsByCollection, featuredItems, isLoading, isError, error } =
    useTransformedMenuData()
  const { restaurant } = useMenuData()

  const [selectedItem, setSelectedItem] = useState<MenuItem | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [activeSection, setActiveSection] = useState<string | null>(null)

  // Set first section active on load
  useEffect(() => {
    if (collections.length > 0 && !activeSection) {
      setActiveSection(collections[0].name)
    }
  }, [collections, activeSection])

  // Sync active category pill with the section currently scrolled into view.
  // Uses a scroll listener instead of IntersectionObserver to avoid jumpy/glitchy
  // highlights caused by batch-fired observer callbacks during fast scrolling.
  useEffect(() => {
    if (!collections.length) return

    let rafId: number

    const handleScroll = () => {
      cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(() => {
        const stickyOffset = 64
        let current = collections[0].name

        for (const collection of collections) {
          const el = document.getElementById(`section-${collection.id}`)
          if (!el) continue
          if (el.getBoundingClientRect().top <= stickyOffset) {
            current = collection.name
          }
        }

        setActiveSection((prev) => (prev === current ? prev : current))
      })
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()

    return () => {
      window.removeEventListener('scroll', handleScroll)
      cancelAnimationFrame(rafId)
    }
  }, [collections])

  const handleSectionClick = (sectionName: string) => {
    const section = collections.find(c => c.name === sectionName)
    if (!section) return

    const el = document.getElementById(`section-${section.id}`)
    if (el) {
      const offset = 64
      const y = el.getBoundingClientRect().top + window.scrollY - offset
      window.scrollTo({ top: y, behavior: 'smooth' })
    }
  }

  const handleItemClick = useCallback((item: MenuItem) => {
    setSelectedItem(item)
    setIsModalOpen(true)
  }, [])

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false)
    setSelectedItem(null)
  }, [])

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20 flex flex-col">
      {/* Navbar sticks within this wrapper — when the wrapper scrolls out,
          the navbar is pushed up by the filters arriving from below */}
      <div>
        <StickyTopBar />
        <div className="container mx-auto px-3 sm:px-4 pt-4 sm:pt-6 max-w-7xl">
          <RestaurantHeader />

          {/* Error State */}
          {isError && (
            <Alert variant="destructive" className="mb-4 sm:mb-6 animate-scale-in">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>{t('common:errorLoadingMenu')}</AlertTitle>
              <AlertDescription>
                {(error as { data?: { message?: string } })?.data?.message || t('common:failedToLoadMenu')}
              </AlertDescription>
            </Alert>
          )}

          {/* Loading State */}
          {isLoading && <MenuLoadingSkeleton />}

          {/* Featured Items Carousel */}
          {!isLoading && featuredItems.length > 0 && (
            <FeaturedCarousel
              items={featuredItems}
              onItemClick={handleItemClick}
            />
          )}
        </div>
      </div>

      {/* Filters + menu content — CategoryNav sticks independently at top-0 */}
      <div className="flex-1">
        <div className="container mx-auto px-3 sm:px-4 max-w-7xl pb-24">
          {/* Section Navigation */}
          {!isLoading && collections.length > 0 && (
            <CategoryNav
              categories={collections.map(c => ({ id: c.id, name: c.name }))}
              activeCategory={activeSection}
              onCategoryClick={handleSectionClick}
            />
          )}

          {/* Menu Sections */}
          {!isLoading && collections.length > 0 && (
            <div className="space-y-8 sm:space-y-10 animate-fade-in">
              {collections.map((collection) => (
                <MenuSection
                  key={collection.id}
                  id={collection.id}
                  name={collection.name}
                  description={collection.description}
                  items={itemsByCollection[collection.id] || []}
                  onItemClick={handleItemClick}
                />
              ))}
            </div>
          )}

          {/* FAQ */}
          {!isLoading && (
            <CheckoutFAQ
              items={restaurant?.faq_items}
              restaurantPhone={restaurant?.phone}
              restaurantEmail={restaurant?.email}
            />
          )}
        </div>
      </div>

      {/* Footer */}
      <Footer />

      {/* Customization Modal */}
      <ItemCustomizationModal
        item={selectedItem}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />

      {/* Cart Sidebar */}
      <CartSidebar onProceedToCheckout={() => onNavigate('/checkout')} />

      {/* Floating Cart Button */}
      <FloatingCartButton />

      {/* Multi-location prompt (first visit only) */}
      <LocationPickerSheet />
    </div>
  )
}
